/**
 * @module Views/PartnerDashboard/SignupVsPaidPartnerUsers
 * 
 * This module displays a table comparing sign-up users vs paid users for a partner system. It includes:
 * - A data table with client details (email, name, phone, PAN, status, service purchased).
 * - Filtering functionality to show clients based on their subscription status (paid/unpaid).
 * - Pagination and sorting functionality to manage the displayed rows.
 * 
 * It uses Material UI components for the layout and custom components for the table and dropdown.
 * 
 * @component
 * @returns {JSX.Element} The rendered SignupVsPaidPartnerUsers component.
 */
import {
    Box,
    Card,
    Chip,
    Table,
    TableBody,
    TableContainer,
    Typography
} from '@mui/material';
import { Dropdown } from 'components/DesignSystem/DropDown';
import { CustomTableCell, CustomTablePagination, CustomTableRow } from 'components/DesignSystem/Table/CustomTable';
import { filter } from 'lodash';
import Scrollbar from 'oldLayouts/components/scrollbar';
import { UserListHead, UserListToolbar } from 'oldLayouts/components/user';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchConversionRatio } from 'services/partnerService';
import PageContainer from 'views/dashboard/PageContainer';

/**
 * Comparator function to sort data in descending order.
 * It handles sorting by service length and default comparison for other fields.
 * 
 * @function descendingComparator
 * @param {Object} a - The first client data object.
 * @param {Object} b - The second client data object.
 * @param {string} orderBy - The field by which to sort the data.
 * @returns {number} - A negative number if a < b, a positive number if a > b, or 0 if equal.
 */
function descendingComparator(a, b, orderBy) {
    if (orderBy === 'service') {
        // Sort by the length of the service array
        const aLength = a[orderBy] ? a[orderBy].length : 0;
        const bLength = b[orderBy] ? b[orderBy].length : 0;
        return bLength - aLength;
    }
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

/**
 * Returns a comparator function to handle sorting based on order (ascending or descending) 
 * and the field to be sorted (e.g., email, name).
 * 
 * @function getComparator
 * @param {string} order - The current sorting order ('asc' or 'desc').
 * @param {string} orderBy - The field by which to sort (e.g., 'email').
 * @returns {function} - Comparator function to be used for sorting.
 */
function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

/**
 * Function to sort and filter the data array based on the provided comparator and query string.
 * It sorts the array first and then applies a text filter if the query string is present.
 * 
 * @function stableSort
 * @param {Array} array - The data array to be sorted.
 * @param {function} comparator - The comparator function used for sorting.
 * @param {string} query - The query string used for filtering the data.
 * @returns {Array} - The sorted and filtered array of clients.
 */
function stableSort(array, comparator, query) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    if (query) {
        return filter(
            array,
            (_user) =>
                JSON.stringify(_user ?? {})
                    .toLowerCase()
                    .indexOf(query.toLowerCase()) !== -1
        );
    }
    return stabilizedThis.map((el) => el[0]);
}

/**
 * Array containing table column headers, used in the table header to define the fields.
 * 
 * @constant
 * @type {Array}
 * @default
 */
const TABLE_HEAD = [
    {
        id: 'email',
        numeric: true,
        label: 'Email',
        alignRight: false,
    },
    {
        id: 'name',
        disablePadding: true,
        label: 'Client Name',
        alignRight: false,
    },
    {
        id: 'phone',
        numeric: true,
        label: 'Phone',
        alignRight: false,
    },
    {
        id: 'pan',
        label: 'PAN',
        alignRight: false,
    },
    {
        id: 'status',
        label: 'Status',
        alignRight: false,
    },
    {
        id: 'service',
        label: 'Service',
        alignCenter: true,
    },
];

/**
 * Dropdown filter options for service subscription status.
 * 
 * @constant
 * @type {Array}
 * @default
 */
const roleSelectOption = [
    {
        label: 'All',
        value: 'all'
    },
    {
        label: 'Paid',
        value: 'paid'
    },
    {
        label: 'Unpaid',
        value: 'unpaid'
    },
]

/**
 * Component to display the comparison between sign-up users and paid users.
 * This component fetches client data, handles sorting, filtering, and pagination, 
 * and renders a table with the necessary client information.
 * 
 * @component
 * @returns {JSX.Element} The rendered SignupVsPaidPartnerUsers component.
 */
const SignupVsPaidPartnerUsers = () => {
    const [order, setOrder] = useState('asc');  // Sorting order
    const [orderBy, setOrderBy] = useState('email');  // Sorting field
    const [page, setPage] = useState(0);  // Current page number
    const [rowsPerPage, setRowsPerPage] = useState(5);  // Rows per page
    const [conversionPageData, setConversionRatioData] = useState([]);  // Fetched client data
    const [filterName, setFilterName] = useState('');  // Name filter for searching
    const [filterValue, setFilterValue] = useState('all');  // Service status filter ('all', 'paid', 'unpaid')

    /**
     * Fetches the client data from the partner service API, processes it, and updates the state.
     * Each client's service is mapped into a Chip component if services are available.
     * 
     * @async
     * @function fetchClientsData
     * @returns {Promise<void>} Resolves once data is fetched and processed.
     */
    const fetchClientsData = async () => {
        try {
            const response = await fetchConversionRatio(true);
            const { conversionRatioData } = response.data;
            // const conversionRatioData = signuppaidDummyData;
            const updatedData =
                conversionRatioData &&
                conversionRatioData?.map((client) => {
                    if (client?.subscriptions?.length > 0) {
                        return {
                            ...client,
                            status: 'Service purchased',
                            service: client?.subscriptions?.map((service) => (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', margin: '4px 0' }}>
                                    <Chip size="small" key={service} label={service} variant="outlined" />
                                </Box>
                            )),
                        };
                    }
                    return {
                        ...client,
                        status: 'Service not purchased',
                        service: 'N/A',
                    };
                });
            setConversionRatioData(updatedData || []);
        } catch (error) {
            console.error('Error fetching conversion ratio data:', error);
        }
    };

    useEffect(() => {
        fetchClientsData();
    }, []);

    /**
     * Filters the client data based on the selected service status filter (paid, unpaid, or all).
     * 
     * @function filteredData
     * @returns {Array} The filtered list of clients.
     */
    const filteredData = useMemo(() => {
        if (filterValue === 'paid') {
            return conversionPageData.filter((client) => client.subscriptions.length > 0);
        }
        if (filterValue === 'unpaid') {
            return conversionPageData.filter((client) => client.subscriptions.length === 0);
        }
        return conversionPageData;
    }, [filterValue, conversionPageData]);

    /**
     * Handles the sorting request when the user clicks on a table header.
     * 
     * @function handleRequestSort
     * @param {object} event - The event object triggered by the sort request.
     * @param {string} property - The property of the data to be sorted.
     */
    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    /**
     * Handles page change in pagination.
     * 
     * @function handleChangePage
     * @param {object} event - The event object triggered by the page change.
     * @param {number} newPage - The new page number.
     */
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    /**
     * Handles change in rows per page for pagination.
     * 
     * @function handleChangeRowsPerPage
     * @param {object} event - The event object triggered by the change in rows per page.
     */
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - conversionPageData.length) : 0;

    const visibleRows = useMemo(
        () =>
            stableSort(filteredData || [], getComparator(order, orderBy), filterName).slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
            ),
        [order, orderBy, page, rowsPerPage, conversionPageData, filterName, filterValue]
    );

    /**
     * Handles input change for the name filter field.
     * 
     * @function handleFilterByName
     * @param {object} event - The event object triggered by the filter input.
     */
    const handleFilterByName = (event) => {
        setPage(0);
        setFilterName(event.target.value);
    };

     /**
     * Handles the change in the dropdown filter for service status.
     * 
     * @function handleFilterChange
     * @param {object} event - The event object triggered by the dropdown change.
     */
    const handleFilterChange = (event) => {
        setFilterValue(event.target.value);
    };

    return (
        <PageContainer maxWidth="xl" sx={{ padding: '24px' }}>
            <Helmet>
                <title> Sign-up Users VS Paid Users | Get Will Done </title>
            </Helmet>
            <Box>
                <Typography variant="title1">Sign-up Users VS Paid Users</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', margin: '24px 0 12px 0' }}>
                <Box>
                    <UserListToolbar filterName={filterName} onFilterName={handleFilterByName} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px' }}>
                    <Typography variant='bodyText4'>Service Purchased</Typography>
                    <Dropdown value={filterValue} handleChange={handleFilterChange} placeholder="Role" options={roleSelectOption} dropdownWidth='100px' />
                </Box>
            </Box>
            <Card elevation={0} sx={{ paddingBottom: '70px' }}>
                <Scrollbar>
                    <TableContainer>
                        <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
                            <UserListHead order={order} orderBy={orderBy} headLabel={TABLE_HEAD} onRequestSort={handleRequestSort} />
                            <TableBody>
                                {visibleRows.map((row, index) => (
                                    <CustomTableRow hover tabIndex={-1} key={row.id}>
                                        <CustomTableCell>
                                            {row.email}
                                        </CustomTableCell>
                                        <CustomTableCell>{row.name}</CustomTableCell>
                                        <CustomTableCell>{row.phone}</CustomTableCell>
                                        <CustomTableCell>{row.pan}</CustomTableCell>
                                        <CustomTableCell>{row.status}</CustomTableCell>
                                        <CustomTableCell align="center">{row.service}</CustomTableCell>
                                    </CustomTableRow>
                                ))}
                                {emptyRows > 0 && (
                                    <CustomTableRow
                                        style={{
                                            height: 53 * emptyRows,
                                        }}
                                    >
                                        <CustomTableCell colSpan={6} />
                                    </CustomTableRow>
                                )}
                            </TableBody>
                            {conversionPageData.length === 0 && (
                                <TableBody>
                                    <CustomTableRow>
                                        <CustomTableCell align="center" colSpan={7} sx={{ py: 3 }}>
                                            No Data Found
                                        </CustomTableCell>
                                    </CustomTableRow>
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>
                </Scrollbar>
                <CustomTablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    component="div"
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Card>
        </PageContainer>
    );
};

export default SignupVsPaidPartnerUsers;